import { useMemo } from 'react';
import { getLanguageLocale } from './config';
import { SHELL_MESSAGES } from './messages';
import { useLanguageStore } from './store';

export interface TranslationParams {
  [key: string]: string | number;
}

const interpolate = (template: string, params: TranslationParams = {}) =>
  template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => String(params[key] ?? ''));

export const createShellTranslator = (language: keyof typeof SHELL_MESSAGES) => {
  const catalog = SHELL_MESSAGES[language] ?? {};
  return (message: string, params?: TranslationParams) => interpolate(catalog[message] ?? message, params);
};

export const translateCurrentShellMessage = (message: string, params?: TranslationParams) =>
  createShellTranslator(useLanguageStore.getState().language)(message, params);

export const useShellI18n = () => {
  const language = useLanguageStore((state) => state.language);

  return useMemo(() => {
    const locale = getLanguageLocale(language);
    const t = createShellTranslator(language);

    return {
      language,
      locale,
      t,
      formatDate: (value: Date | string) =>
        new Intl.DateTimeFormat(locale).format(typeof value === 'string' ? new Date(value) : value),
      formatDateTime: (value: Date | string) =>
        new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(typeof value === 'string' ? new Date(value) : value),
      formatNumber: (value: number) => new Intl.NumberFormat(locale).format(value),
      formatCurrency: (value: number, currency: string = 'EUR') =>
        new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(value),
    };
  }, [language]);
};
